// voiceTurn — hands-free turn policy for SessionWorkspace.
// Kept out of SessionWorkspace.jsx so the spine DP/LOC ratchet stays green.
//
// Conversational voice MUST feel like a phone call: Realtime STT → one LLM
// shot with NO tools and a tiny system prompt → TTS on the first sentence.
// The old path (full constitution + "1ª resposta = tool calls" + read tools)
// made the model narrate auth/adapters and burn seconds on tool rounds.

/**
 * @returns {boolean}
 */
export function isHandsFreeActive() {
  return typeof window !== 'undefined' && !!(/** @type {*} */ (window).__apoloHandsFree);
}

/**
 * Creator mode: the voice DRIVES the full text agent — write code, run tools,
 * full context — instead of the fast read-only conversational turn. Toggled in
 * the voice modal; mirrored to a window flag so this pure module can read it.
 * @returns {boolean}
 */
function isVoiceCreatorActive() {
  return typeof window !== 'undefined' && !!(/** @type {*} */ (window).__apoloVoiceCreator);
}

/**
 * Conversational voice turns must stay low-latency: no vault, no verifiers,
 * no tools, no heavy system prompt, one LLM iteration. Plan mode still wins.
 *
 * @param {boolean} planPropose
 * @param {number} defaultMaxIterations
 */
export function voiceTurnPolicy(planPropose, defaultMaxIterations) {
  const voiceMode = isHandsFreeActive();
  // Creator: voice conducts the FULL agent (tools, multi-iteration, full context
  // + heavy system) — "faz tudo". Voice-only stays the fast, read-only,
  // conversational turn. Plan mode always wins over both.
  const creator = voiceMode && isVoiceCreatorActive();
  const conversational = voiceMode && !planPropose && !creator;
  return {
    voiceMode,
    creator,
    conversational,
    maxIterations: conversational ? 1 : defaultMaxIterations,
    forceReadOnly: conversational,
    disableTools: conversational,
    // Creator loads the same context/verifiers as a normal turn (keyed off
    // conversational, not voiceMode, so voice-only and non-voice are unchanged).
    loadAmbient: !conversational,
    loadVerifiers: !conversational,
    skipHeavySystem: conversational,
  };
}

/** Read-only flags for doctrine injection + agent loop (keeps SessionWorkspace DP flat). */
export function voiceReadFlags(vt, askMode) {
  return {
    // Creator voice can WRITE, so its doctrine must not be read-only — only a
    // read-only (voice-only / plan) voice turn or ask-mode locks it down.
    doctrineReadOnly: !!(askMode || (vt.voiceMode && !vt.creator)),
    loopReadOnly: !!(askMode || vt.forceReadOnly),
  };
}

/**
 * Skip the expensive POST /system-prompt round-trip on conversational voice.
 * @param {{ skipHeavySystem?: boolean }} vt
 * @param {() => Promise<string>} fetchFn
 * @returns {Promise<string>}
 */
export function maybeVoiceSkipSystemPrompt(vt, fetchFn) {
  if (vt?.skipHeavySystem) return Promise.resolve('');
  return fetchFn();
}

/**
 * Skip constitution dump on conversational voice (it talks about adapters/auth).
 * @param {{ skipHeavySystem?: boolean }} vt
 * @param {() => string} build
 * @returns {string}
 */
export function maybeVoiceSkipConstitution(vt, build) {
  if (vt?.skipHeavySystem) return '';
  return build() || '';
}

/**
 * System prompt for the turn: voice = date + MODO VOZ only; else heavy stack.
 * @param {{ conversational?: boolean }} vt
 * @param {{ heavySys?: string, agentRulesBlock?: string, ambientBlock?: string, directive?: string|null, dateLine?: string }} parts
 */
export function voiceComposeSystem(vt, parts) {
  const directive = parts?.directive || '';
  if (vt?.conversational) {
    return [parts?.dateLine, directive].filter(Boolean).join('\n\n');
  }
  return [parts?.heavySys, parts?.agentRulesBlock, parts?.ambientBlock, directive]
    .filter(Boolean)
    .join('\n\n');
}

/**
 * User task for the turn. Conversational voice MUST NOT wrap with buildTaskAnchor
 * ("1ª resposta = tool calls") — that is what made the model narrate tools/auth.
 * @param {{ conversational?: boolean }} vt
 * @param {{ autoRound: number, originalTask: string, planPropose: boolean, buildTaskAnchor: Function, buildAutoContinuePrompt: Function }} args
 */
export function voiceRoundTask(vt, args) {
  const { autoRound, originalTask, planPropose, buildTaskAnchor, buildAutoContinuePrompt } = args;
  if (autoRound !== 0) return buildAutoContinuePrompt(originalTask, autoRound);
  if (vt?.conversational) return String(originalTask || '').trim();
  return buildTaskAnchor(originalTask, { planMode: planPropose }) || originalTask;
}

/**
 * Extra tools (harvest/subagents) — never on conversational voice.
 * @param {{ conversational?: boolean }} vt
 * @param {any[]} tools
 */
export function voiceFilterExtraTools(vt, tools) {
  if (vt?.conversational) return [];
  return Array.isArray(tools) ? tools : [];
}

/**
 * excludeTools sentinel for the agent loop — '*' wipes the whole toolset.
 * @param {{ disableTools?: boolean }} vt
 * @returns {string[]}
 */
export function voiceExcludeTools(vt) {
  return vt?.disableTools ? ['*'] : [];
}

/**
 * Kind override so toolless replies settle as conversational_done (no tool nudges).
 * @param {{ conversational?: boolean }} vt
 * @param {string|null} sessionKind
 */
export function voiceSessionKind(vt, sessionKind) {
  return vt?.conversational ? 'chat' : sessionKind;
}

/**
 * Cap recent chat history for voice — drop tool dumps that bias the model
 * toward narrating infrastructure.
 * @param {{ conversational?: boolean }} vt
 * @param {Array<{role?: string, content?: any}>} history
 */
export function voiceCapHistory(vt, history) {
  if (!vt?.conversational) return history;
  const list = Array.isArray(history) ? history : [];
  return list
    .filter((m) => m && (m.role === 'user' || m.role === 'assistant' || m.role === 'agent'))
    .slice(-6)
    .map((m) => ({
      role: m.role === 'agent' ? 'assistant' : m.role,
      content: String(m.content || '').slice(0, 600),
    }));
}

/**
 * Team mode is incompatible with instant voice replies.
 * @param {{ conversational?: boolean }} vt
 */
export function voiceAllowsTeamTurn(vt) {
  return !vt?.conversational;
}

/**
 * Resolve ambient + verifier prep for a turn in one place (keeps SessionWorkspace thin).
 *
 * @param {{
 *   planPropose: boolean,
 *   defaultMaxIterations: number,
 *   task: string,
 *   sessionId: string,
 *   maybeAmbientBrain: Function,
 *   isAmbientEnabled: Function,
 *   buildAutoCorrect: () => Promise<any>,
 * }} deps
 */
export async function prepareVoiceAwareTurn({
  planPropose,
  defaultMaxIterations,
  task,
  sessionId,
  maybeAmbientBrain,
  isAmbientEnabled,
  buildAutoCorrect,
}) {
  const vt = voiceTurnPolicy(planPropose, defaultMaxIterations);
  const ambientBlock = vt.loadAmbient
    ? await maybeAmbientBrain(task, sessionId, { enabled: isAmbientEnabled() })
    : null;
  let autoCorrectCfg = null;
  if (vt.loadVerifiers) {
    try {
      autoCorrectCfg = await buildAutoCorrect();
    } catch {
      autoCorrectCfg = null;
    }
  }
  return { vt, ambientBlock, autoCorrectCfg };
}
